import React from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import BillingTab from './components/BillingTab/BillingTab';
import AffiliatesTab from './components/AffiliatesTab';
import CustomEmojiTab from './components/CustomEmojiTab/CustomEmojiTab';
import PrivacyTab from './components/PrivacyTab/PrivacyTab';
import BlockedUsersTab from './components/BlockedUsersTab/BlockedUsersTab';
import AccountTab from './components/AccountTab/AccountTab';
import ProfileTab from './components/ProfileTab/ProfileTab';
import PreferencesTab from './components/PreferencesTab/PreferencesTab';
import NotificationsTab from './components/NotificationsTab/NotificationsTab';
import { SETTINGS_TABS, SettingsTabKey, resolveSettingsTab } from './settingsTabs';
import './Settings.css';

const TAB_COMPONENTS: Record<SettingsTabKey, React.ComponentType> = {
  profile: ProfileTab,
  account: AccountTab,
  preferences: PreferencesTab,
  notifications: NotificationsTab,
  privacy: PrivacyTab,
  blocked: BlockedUsersTab,
  emoji: CustomEmojiTab,
  partners: AffiliatesTab,
  billing: BillingTab,
};

/**
 * Settings shell: one horizontal tab bar (the same on desktop and phone, it
 * scrolls sideways when it does not fit) above the active tab. The URL
 * (/settings/:tab?) decides the tab, so direct loads, tab clicks and browser
 * back/forward agree. Slugs from before the redesign redirect to their new tab.
 */
export default function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const { tab: activeTab, redirectTo } = resolveSettingsTab(
    tabParam,
    (location.state as { tab?: string } | null)?.tab,
  );

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  const ActiveTab = TAB_COMPONENTS[activeTab];

  return (
    <div className="settings-layout">
      <nav className="settings-tabbar" aria-label="Settings sections" data-testid="settings-tabbar">
        {SETTINGS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`settings-tabbar__tab ${activeTab === tab.key ? 'settings-tabbar__tab--active' : ''}`}
            aria-current={activeTab === tab.key ? 'page' : undefined}
            onClick={() => navigate(`/settings/${tab.key}`)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className={`settings-body settings-body--${activeTab}`}>
        <div className="settings-form">
          <ActiveTab />
        </div>
      </div>
    </div>
  );
}
